import { find, get, includes, isFunction } from 'lodash'
import { GooglePlaceDetail, PlaceType } from './types'
import { Address, AddressPosition } from '../../types'

/**
 * Finds a specific component of an address from Google Place details.
 *
 * @param {GooglePlaceDetail} details - The details of the Google Place.
 * @param {PlaceType} placeType - The type of place to search for in the address components.
 * @param {'short_name' | 'long_name'} [path='short_name'] - The path to retrieve from the address component.
 * @returns {string} The found address component or an empty string if not found.
 */
export const findAddressComponent = (
  details: GooglePlaceDetail,
  placeType: PlaceType,
  path: 'short_name' | 'long_name' = 'short_name'
): string => {
  const addressComponents = get(details, 'address_components')
  if (addressComponents) {
    return get(
      find(addressComponents, (d) => includes(d.types, placeType)),
      path,
      ''
    )
  }
  return ''
}

/**
 * Retrieves the geographic position (latitude and longitude) from a Google Place.
 *
 * @param {GooglePlaceDetail} place - The details of the Google Place.
 * @returns {AddressPosition} The geographic position of the place.
 */
export const getPlacePosition = (place: GooglePlaceDetail): AddressPosition => {
  const lng = get(place, 'geometry.location.lng')
  const lat = get(place, 'geometry.location.lat')
  return {
    type: 'Point',
    coordinates: [isFunction(lng) ? lng() : lng, isFunction(lat) ? lat() : lat],
  }
}

/**
 * Formats the address from Google Place details into a structured Address object.
 *
 * @param {GooglePlaceDetail} place - The details of the Google Place.
 * @returns {Address} The formatted address.
 */
export const getAddressFromGooglePlaceDetail = (place: GooglePlaceDetail): Address => {
  return {
    province: findAddressComponent(place, 'administrative_area_level_2', 'long_name'),
    provinceCode: findAddressComponent(place, 'administrative_area_level_2', 'short_name'),
    region:
      findAddressComponent(place, 'administrative_area_level_1', 'long_name') ||
      findAddressComponent(place, 'postal_town', 'long_name'),
    regionCode:
      findAddressComponent(place, 'administrative_area_level_1', 'short_name') ||
      findAddressComponent(place, 'postal_town', 'short_name'),
    country: findAddressComponent(place, 'country', 'long_name'),
    countryCode: findAddressComponent(place, 'country', 'short_name'),
    streetName: findAddressComponent(place, 'route', 'long_name'),
    streetNumber: findAddressComponent(place, 'street_number'),
    zipCode: findAddressComponent(place, 'postal_code'),
    city: findAddressComponent(place, 'administrative_area_level_3', 'long_name'),
    cityCode: findAddressComponent(place, 'administrative_area_level_3', 'short_name'),
    plainAddress: get(place, 'formatted_address'),
    position: getPlacePosition(place),
    placeId: get(place, 'place_id'),
  }
}
